import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Copy, Check, ShoppingBag, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal, { SectionLabel } from "../components/Reveal";

const inputCls =
  "mt-2 w-full rounded-xl border border-line bg-ink px-4 py-3 font-mono text-xs text-white outline-none transition-colors focus:border-signal";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-muted";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const statusChip = (s) => {
  const map = {
    pending: { icon: Clock, cls: "border-gold/40 bg-gold/10 text-gold" },
    approved: { icon: CheckCircle2, cls: "border-profit/40 bg-profit/10 text-profit" },
    rejected: { icon: XCircle, cls: "border-loss/40 bg-loss/10 text-loss" },
  };
  const m = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] font-600 uppercase tracking-[0.12em] ${m.cls}`}>
      <m.icon size={10} /> {s}
    </span>
  );
};

export default function Earn() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [firms, setFirms] = useState([]);
  const [copied, setCopied] = useState(false);
  const [rd, setRd] = useState({ firm_slug: "", account_size: "", amount_coins: "" });
  const [rdBusy, setRdBusy] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/auth?next=/earn", { replace: true });
  }, [user, navigate]);

  const load = () => {
    api.get("/coins/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    load();
    api.get("/firms").then(({ data }) => setFirms(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/auth?ref=${wallet.referral.code}`);
      setCopied(true);
      toast.success("REFERRAL LINK COPIED");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("COPY FAILED — SELECT THE LINK MANUALLY");
    }
  };

  const submitRedeem = async () => {
    const amount = parseFloat(rd.amount_coins);
    if (!amount || amount < (wallet?.min_redemption || 5)) {
      toast.error(`MINIMUM REDEMPTION IS ${wallet?.min_redemption || 5} COINS`);
      return;
    }
    if (!rd.firm_slug || !rd.account_size.trim()) {
      toast.error("PICK A FIRM AND ACCOUNT SIZE");
      return;
    }
    setRdBusy(true);
    try {
      await api.post("/coins/redeem", {
        firm_slug: rd.firm_slug,
        account_size: rd.account_size,
        amount_coins: amount,
      });
      setRd({ firm_slug: "", account_size: "", amount_coins: "" });
      toast.success("REDEMPTION REQUESTED — THE DESK WILL DELIVER YOUR ACCOUNT CREDIT");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRdBusy(false);
    }
  };

  const ref = wallet?.referral;

  return (
    <div data-testid="earn-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="scanlines-overlay" />
        <div className="absolute -right-24 top-0 h-64 w-64 rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-5xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Referral Income</SectionLabel>
            <h1 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
              Refer & <span className="text-gradient">earn</span>
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted">
              Invite traders and you both earn 0.15 FTC coin instantly. Referral coins are account credit — they can't be withdrawn as cash, but they buy accounts at any listed firm.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
        {ref && (
          <div className="card-violet p-6 sm:p-7" data-testid="referral-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Your Invite Link"}</p>
            <h2 className="mt-2 flex items-center gap-2 font-display text-xl font-800 text-white">
              <Gift size={18} className="text-gold" /> Invite traders, earn coins
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Share your link — when a friend joins, you both get 0.15 coin instantly. Earn up to 15 coins per month during your first 3 months.
            </p>
            <div className="mt-4 flex gap-2">
              <input readOnly value={`${window.location.origin}/auth?ref=${ref.code}`}
                onFocus={(e) => e.target.select()}
                className="w-full rounded-xl border border-line bg-ink px-4 py-3 font-mono text-xs text-white outline-none"
                data-testid="referral-link-input" />
              <button onClick={copyReferral}
                className="btn-gradient inline-flex shrink-0 items-center gap-1.5 rounded-xl px-5 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-white"
                data-testid="referral-copy-btn">
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="mt-5">
              <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                <span>This month</span>
                <span data-testid="referral-earned-month">{ref.earned_this_month} / {ref.monthly_cap} coins</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-ink">
                <div className="h-full rounded-full bg-gradient-to-r from-[#3b82f6] to-[#8b5cf6] transition-all duration-500"
                  style={{ width: `${ref.earned_this_month > 0 ? Math.max(3, Math.min(100, (ref.earned_this_month / ref.monthly_cap) * 100)) : 0}%` }}
                  data-testid="referral-progress" />
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-line bg-ink/60 p-3.5 text-center">
                <p className="font-display text-2xl font-800 text-white" data-testid="referral-invites-count">{ref.invites}</p>
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Invites</p>
              </div>
              <div className="rounded-xl border border-line bg-ink/60 p-3.5 text-center">
                <p className="font-display text-2xl font-800 text-gold" data-testid="referral-total">{ref.total_earned}</p>
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Coins earned</p>
              </div>
              <div className="rounded-xl border border-line bg-ink/60 p-3.5 text-center">
                <p className="font-display text-2xl font-800 text-white" data-testid="referral-credit">{wallet?.referral_credit ?? "…"}</p>
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Credit left</p>
              </div>
              <div className="rounded-xl border border-line bg-ink/60 p-3.5 text-center">
                <p className={`font-display text-2xl font-800 ${ref.eligible ? "text-profit" : "text-loss"}`} data-testid="referral-status">
                  {ref.eligible ? "LIVE" : "ENDED"}
                </p>
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Reward status</p>
              </div>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-muted" data-testid="referral-window">
              {ref.eligible
                ? `Rewards active until ${fmtDate(ref.window_ends_at)} · 0.15 coin per invite, friend gets 0.15 too`
                : "Your 3-month referral window has ended"}
            </p>
            {ref.recent.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="referral-history">
                {ref.recent.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 border-b border-line/50 pb-2 font-mono text-xs" data-testid={`referral-row-${r.id}`}>
                    <span className="text-muted">{r.referee_name} joined</span>
                    <span className="flex items-center gap-4">
                      <span className="text-muted">{fmtDate(r.created_at)}</span>
                      <span className={`font-700 ${r.reward > 0 ? "text-profit" : "text-muted"}`}>+{r.reward}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 grid items-start gap-6 lg:grid-cols-2">
          <div className="card-violet p-6 sm:p-7" data-testid="redeem-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Spend Referral Coins"}</p>
            <h2 className="mt-2 font-display text-xl font-800 text-white">Redeem for an account</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Turn referral coins into account credit at any listed firm. Minimum {wallet?.min_redemption || 5} coins — the desk reviews and delivers manually.
            </p>
            <p className="mt-3 font-mono text-[11px] text-gold" data-testid="redeem-available">
              Available referral credit: {wallet ? wallet.referral_credit : "…"} coins
            </p>
            <div className="mt-4">
              <label className={labelCls}>Firm</label>
              <select value={rd.firm_slug} onChange={(e) => setRd({ ...rd, firm_slug: e.target.value })}
                data-testid="redeem-firm-select" className={inputCls}>
                <option value="">Select firm…</option>
                {firms.map((f) => <option key={f.slug} value={f.slug}>{f.name}</option>)}
              </select>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Account size</label>
                <input value={rd.account_size} onChange={(e) => setRd({ ...rd, account_size: e.target.value })}
                  data-testid="redeem-size-input" placeholder="$100K" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Coins to use</label>
                <input value={rd.amount_coins} onChange={(e) => setRd({ ...rd, amount_coins: e.target.value.replace(/[^0-9.]/g, "") })}
                  data-testid="redeem-amount-input" placeholder="5" inputMode="decimal" className={inputCls} />
              </div>
            </div>
            <button onClick={submitRedeem} disabled={rdBusy} data-testid="redeem-submit-btn"
              className="btn-beam btn-gradient mt-5 inline-flex w-full items-center justify-center gap-2 px-5 py-3.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] text-white disabled:opacity-50">
              <ShoppingBag size={13} /> {rdBusy ? "Requesting…" : "Request Account Credit"}
            </button>
          </div>

          <div className="card-violet p-6 sm:p-7" data-testid="redemptions-history">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// My Redemptions"}</p>
            <div className="mt-4 space-y-3">
              {!wallet ? null : wallet.redemptions.length === 0 ? (
                <p className="font-mono text-xs text-muted">No redemptions yet — invite friends, stack coins, grab an account.</p>
              ) : (
                wallet.redemptions.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-ink px-4 py-3.5" data-testid={`redemption-row-${r.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-600 text-white">{r.firm_name} · {r.account_size}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-muted">{fmtDate(r.created_at)} · {r.amount_coins} coins</p>
                      {r.admin_note && <p className="mt-1 font-mono text-[10px] text-loss">{r.admin_note}</p>}
                    </div>
                    {statusChip(r.status)}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
